import { PhotoIcon } from '@heroicons/react/24/outline';
import { IVideoClipResource, ResourceType } from '@packages/global';
import { useTranslation } from 'react-i18next';
import ImageResourceItem from './ImageResourceItem';

interface ImageResourceListProps {
  resources: IVideoClipResource[];
  frameType: 'start' | 'end' | 'storyboard';
  canReference: boolean;
  onApplyStart: (imageUrl: string) => void;
  onApplyEnd: (imageUrl: string) => void;
  onApplyStoryboard?: (imageUrl: string) => void;
  onReference: (imageUrl: string) => void;
  onReuse: (params: any) => void;
  onDelete: (resourceId: string) => void;
}

const getResourceType = (frameType: 'start' | 'end' | 'storyboard'): ResourceType => {
  switch (frameType) {
    case 'start': return ResourceType.START_IMAGE;
    case 'end': return ResourceType.END_IMAGE;
    case 'storyboard': return ResourceType.STORYBOARD_IMAGE;
  }
};

const FRAME_TYPE_LABEL_KEY: Record<string, string> = {
  start: 'imagesTab.startFrame',
  end: 'imagesTab.endFrame',
  storyboard: 'imagesTab.storyboardImage'
};

export default function ImageResourceList({
  resources,
  frameType,
  canReference,
  onApplyStart,
  onApplyEnd,
  onApplyStoryboard,
  onReference,
  onReuse,
  onDelete
}: ImageResourceListProps) {
  const { t } = useTranslation('editor');

  const filteredResources = resources
    .filter((r) => r.resourceType === getResourceType(frameType))
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  if (filteredResources.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <PhotoIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('imageGenForm.noRecords', { frameType: t(FRAME_TYPE_LABEL_KEY[frameType]) })}</p>
        <p className="text-xs mt-1">{t('imageGenForm.startBelow')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {filteredResources.map((resource) => (
        <ImageResourceItem
          key={resource.id}
          resource={resource}
          canReference={canReference}
          frameType={frameType}
          onApplyStart={onApplyStart}
          onApplyEnd={onApplyEnd}
          onApplyStoryboard={onApplyStoryboard}
          onReference={onReference}
          onReuse={onReuse}
          onDelete={() => onDelete(resource.id)}
        />
      ))}
    </div>
  );
}
